import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import Confetti from "@/components/Confetti";
import CameraScanner from "@/components/CameraScanner";
import { useAuth } from "@/context/AuthContext";
import { claimsApi } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ScanLine, Check, AlertTriangle, ArrowRight, Camera } from "lucide-react";

export default function Scanner() {
  const { user, loading } = useAuth();
  const [code, setCode] = useState("");
  const [sale, setSale] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const [recent, setRecent] = useState([]);
  const [camOpen, setCamOpen] = useState(false);

  if (loading) return <div className="min-h-screen"><NavBar /><div className="text-center py-20">Loading...</div></div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  if (user.role !== "dispensary" && user.role !== "admin") {
    return <div className="min-h-screen"><NavBar /><div className="max-w-2xl mx-auto py-32 text-center"><h2 className="font-display text-4xl font-extrabold mb-3">Shop staff only</h2></div></div>;
  }

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      let geo = {};
      if (navigator.geolocation) {
        await new Promise((res) => {
          navigator.geolocation.getCurrentPosition((p) => { geo = { lat: p.coords.latitude, lng: p.coords.longitude }; res(); }, () => res(), { timeout: 2500 });
        });
      }
      const r = await claimsApi.redeem(code.trim().toUpperCase(), parseFloat(sale) || undefined, geo);
      setSuccess(r);
      setRecent([{ code: code.trim().toUpperCase(), commission: r.commission_charged, balance: r.wallet_balance, when: new Date(), flag: r.flag }, ...recent].slice(0, 5));
      setConfetti(true);
      setTimeout(() => setConfetti(false), 1800);
      setCode(""); setSale("");
      toast.success(`Redeemed · -$${r.commission_charged} commission`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {confetti && <Confetti />}
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -100, left: -80 }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-12 relative">
        <span className="sticker sticker-magenta mb-3 inline-block"><ScanLine size={11} strokeWidth={2.4} /> Scanner</span>
        <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.92] mt-3 mb-3">
          Scan it. <span className="text-rainbow">Done.</span>
        </h1>
        <p className="text-base text-[#0B0F0C]/65 mb-8">Enter the customer's code (or paste from QR scan). Redemption auto-charges your wallet.</p>

        <div className="card-x card-green bg-[#FAF6E8] p-7 mb-6">
          <div className="label-overline text-[#FF1A8C] mb-3">Claim code</div>
          <input
            data-testid="scanner-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="X-AB12-CD34"
            className="w-full bg-white border-2 border-[#0B0F0C] rounded-2xl px-5 h-16 font-mono-x text-3xl font-bold tracking-widest outline-none focus:shadow-[4px_4px_0_#0B0F0C] transition-all uppercase placeholder:text-[#0B0F0C]/30"
          />
          <div className="grid grid-cols-2 gap-3 mt-4">
            <input
              data-testid="scanner-sale"
              type="number" step="0.01" value={sale}
              onChange={(e) => setSale(e.target.value)}
              placeholder="Sale amount $ (optional)"
              className="bg-white border-2 border-[#0B0F0C] rounded-full px-5 h-12 font-mono-x font-bold outline-none focus:shadow-[3px_3px_0_#0B0F0C] transition-all"
            />
            <button data-testid="scanner-redeem" onClick={redeem} disabled={busy || !code.trim()} className="btn-x btn-primary disabled:opacity-50">
              {busy ? "Redeeming..." : "Redeem"} <ArrowRight size={14} strokeWidth={2.4} />
            </button>
          </div>
          <button
            data-testid="scanner-camera-open"
            onClick={() => setCamOpen(true)}
            className="mt-3 w-full btn-x btn-ghost !py-2.5 !text-sm flex items-center justify-center gap-2"
          >
            <Camera size={14} strokeWidth={2.4} /> Or scan with camera
          </button>
        </div>

        {camOpen && (
          <CameraScanner
            onClose={() => setCamOpen(false)}
            onScan={(text) => {
              setCamOpen(false);
              // Extract code if it's a URL containing ?code=
              const m = text.match(/[?&]code=([^&\s]+)/i);
              const cleaned = (m ? m[1] : text).trim().toUpperCase();
              setCode(cleaned);
              toast.success(`Captured ${cleaned}`);
            }}
          />
        )}

        {success && (
          <div className="card-x card-green bg-[#15FF73] p-7 mb-6 relative">
            <div className="flex items-center gap-3 mb-3"><Check size={28} strokeWidth={3} /><span className="font-display text-3xl font-extrabold">Redeemed!</span></div>
            <div className="grid grid-cols-3 gap-3">
              <div><div className="label-overline">Commission</div><div className="font-display text-2xl font-extrabold">${success.commission_charged}</div></div>
              <div><div className="label-overline">Wallet</div><div className="font-display text-2xl font-extrabold">${success.wallet_balance}</div></div>
              <div><div className="label-overline">Status</div><div className="font-display text-2xl font-extrabold">{success.flag ? "⚠️" : "✓"}</div></div>
            </div>
            {success.flag && <div className="mt-3 text-xs font-mono-x font-bold uppercase tracking-widest text-[#0B0F0C] flex items-center gap-1"><AlertTriangle size={12} /> {success.flag}</div>}
          </div>
        )}

        <div className="card-x card-cyan bg-[#FAF6E8] p-6">
          <div className="label-overline text-[#FF1A8C] mb-3">Recent scans</div>
          {recent.length === 0 ? (
            <div className="text-sm text-[#0B0F0C]/55">No scans yet today.</div>
          ) : (
            <div className="space-y-2">
              {recent.map((r, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] p-3">
                  <span className="font-mono-x font-bold tracking-widest">{r.code}</span>
                  <span className="ml-auto font-mono-x text-sm">-${r.commission}</span>
                  <span className="font-mono-x text-xs opacity-60">→ ${r.balance}</span>
                  {r.flag && <span className="sticker sticker-magenta !text-[10px]">flag</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
